import React, {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import {
  LinearProgress,
  styled,
  Avatar,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  ListItemAvatar,
} from '@mui/material';

// Material-UI Icons
import Flip from '@mui/icons-material/Flip';

// Material-UI Icons: Practice
import GridOn from '@mui/icons-material/GridOn';
import HelpOutline from '@mui/icons-material/HelpOutline';
import LocationCity from '@mui/icons-material/LocationCity';
import LocalFlorist from '@mui/icons-material/LocalFlorist';
import LocalShipping from '@mui/icons-material/LocalShipping';
import School from '@mui/icons-material/School';
import Waves from '@mui/icons-material/Waves';

// Material-UI Icons: Practice (Programs)
import DirectionsBoat from '@mui/icons-material/DirectionsBoat';
import Flight from '@mui/icons-material/Flight';
// import HelpOutline from '@mui/icons-material/HelpOutline';
// import LocationCity from '@mui/icons-material/LocationCity';
import LocalDrink from '@mui/icons-material/LocalDrink';
// import LocalFlorist from '@mui/icons-material/LocalFlorist';
// import LocalShipping from '@mui/icons-material/LocalShipping';
import RestoreFromTrash from '@mui/icons-material/RestoreFromTrash';
import Train from '@mui/icons-material/Train';
// import Waves from '@mui/icons-material/Waves';
// import Work from '@mui/icons-material/Work';

// Custom Components
import SmartError from './SmartError';

import useDataSource from '../../../hooks/useDataSource';
import {NavLink} from "react-router-dom";
import {useSelector} from "react-redux";


const PREFIX = 'MagicMatrixCard';

const classes = {
  root: `${PREFIX}-root`,
  linearProgress: `${PREFIX}-linearProgress`,
  popover: `${PREFIX}-popover`,
  paper: `${PREFIX}-paper`,
  containerBorder: `${PREFIX}-containerBorder`,
  fontWeight500: `${PREFIX}-fontWeight500`,
  fontStyleItalic: `${PREFIX}-fontStyleItalic`,
  fontColorWhite: `${PREFIX}-fontColorWhite`,
  fontColorBlack: `${PREFIX}-fontColorBlack`,
  gray: `${PREFIX}-gray`,
  headerContainer: `${PREFIX}-headerContainer`,
  headerContainerWhite: `${PREFIX}-headerContainerWhite`,
  contentContainerWhite: `${PREFIX}-contentContainerWhite`,
  headerContainerGrey: `${PREFIX}-headerContainerGrey`,
  contentContainerGrey: `${PREFIX}-contentContainerGrey`,
  headerContainerRed: `${PREFIX}-headerContainerRed`,
  contentContainerRed: `${PREFIX}-contentContainerRed`,
  headerContainerAmber: `${PREFIX}-headerContainerAmber`,
  contentContainerAmber: `${PREFIX}-contentContainerAmber`,
  headerContainerGreen: `${PREFIX}-headerContainerGreen`,
  contentContainerGreen: `${PREFIX}-contentContainerGreen`,
  positionContainer: `${PREFIX}-positionContainer`,
  positionContent: `${PREFIX}-positionContent`,
  complexityContainer: `${PREFIX}-complexityContainer`,
  decisionMakingContainer: `${PREFIX}-decisionMakingContainer`,
  contentContainer: `${PREFIX}-contentContainer`,
  list: `${PREFIX}-list`,
  listItemPrimary: `${PREFIX}-listItemPrimary`,
  listItemSecondary: `${PREFIX}-listItemSecondary`,
  actionsContainer: `${PREFIX}-actionsContainer`,
  education: `${PREFIX}-education`,
  environmental: `${PREFIX}-environmental`,
  networkManagement: `${PREFIX}-networkManagement`,
  transportation: `${PREFIX}-transportation`,
  water: `${PREFIX}-water`,
  aviation: `${PREFIX}-aviation`,
  conveyanceAndStorage: `${PREFIX}-conveyanceAndStorage`,
  drinkingWaterAndReuse: `${PREFIX}-drinkingWaterAndReuse`,
  highwaysAndBridges: `${PREFIX}-highwaysAndBridges`,
  portsAndMaritime: `${PREFIX}-portsAndMaritime`,
  transitAndRail: `${PREFIX}-transitAndRail`,
  urbanDevelopment: `${PREFIX}-urbanDevelopment`,
  wastewater: `${PREFIX}-wastewater`,
  waterResourcesEcosystemMgmt: `${PREFIX}-waterResourcesEcosystemMgmt`,
  noDataAvailable: `${PREFIX}-noDataAvailable`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')((
  {
    theme
  }
) => ({
  [`&.${classes.root}`]: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: '0px',
    padding: '5px'
  },

  [`& .${classes.linearProgress}`]: {
    height: '1px'
  },

  [`& .${classes.popover}`]: {
    pointerEvents: 'none',
  },

  [`& .${classes.paper}`]: {
    // ...tooltipContent.paper,
    padding: theme.spacing(1),
    maxWidth: "300px"
  },

  [`& .${classes.containerBorder}`]: {
    // border: '1px solid gray'
  },

  [`& .${classes.fontWeight500}`]: {
    fontWeight: 500
  },

  [`& .${classes.fontStyleItalic}`]: {
    fontStyle: 'italic'
  },

  [`& .${classes.fontColorWhite}`]: {
    color: '#FFFFFF'
  },

  [`& .${classes.fontColorBlack}`]: {
    color: '#000000'
  },

  [`& .${classes.gray}`]: {
    backgroundColor: '#B0B0B0'
  },

  // Header
  [`& .${classes.headerContainer}`]: {
    width: 'calc(100% - 30px)',
    height: '60px'
  },

  [`& .${classes.headerContainerWhite}`]: {
    backgroundColor: '#FFFFFF',
    borderColor: '#757575'
  },

  [`& .${classes.contentContainerWhite}`]: {
    backgroundColor: '#FFFFFF',
  },

  [`& .${classes.headerContainerGrey}`]: {
    backgroundColor: '#E0E0E0',
    borderColor: '#757575'
  },

  [`& .${classes.contentContainerGrey}`]: {
    backgroundColor: '#E0E0E0',
  },

  [`& .${classes.headerContainerRed}`]: {
    backgroundColor: '#b71c1c',
    borderColor: '#b71c1c'
  },

  [`& .${classes.contentContainerRed}`]: {
    backgroundColor: '#ffebee'
  },

  [`& .${classes.headerContainerAmber}`]: {
    backgroundColor: '#FF6F00',
    borderColor: '#FF6F00'
  },

  [`& .${classes.contentContainerAmber}`]: {
    backgroundColor: '#FFF8E1'
  },

  [`& .${classes.headerContainerGreen}`]: {
    backgroundColor: '#1B5E20',
    borderColor: '#1B5E20'
  },

  [`& .${classes.contentContainerGreen}`]: {
    backgroundColor: '#E8F5E9'
  },

  // Header: Position
  [`& .${classes.positionContainer}`]: {
    width: '60px',
    position: 'relative',
  },

  [`& .${classes.positionContent}`]: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)'
  },

  // Header: Complexity
  [`& .${classes.complexityContainer}`]: {
    width: 'calc(50% - 30px)'
  },

  // Header: Decision-making
  [`& .${classes.decisionMakingContainer}`]: {
    width: 'calc(50% - 30px)'
  },

  // Content
  [`& .${classes.contentContainer}`]: {
    height: 'calc(100% - 100px)',
    paddingTop: '10px',
    overflowY: 'auto'
  },

  [`& .${classes.list}`]: {
    height: '100%',
    width: '100%',
    padding: '0'
  },

  [`& .${classes.listItemPrimary}`]: {
    // lineHeight: '1.5',
    fontWeight: 'bold'
  },

  [`& .${classes.listItemSecondary}`]: {
    // lineHeight: '1'
  },

  // Actions
  [`& .${classes.actionsContainer}`]: {
    height: '40px',
    padding: '4px 4px 8px 4px',
    display: 'block',
    textAlign: 'right'
  },

  // Avatar colors for CoB
  [`& .${classes.education}`]: {
    backgroundColor: '#9C27B0'
  },

  [`& .${classes.environmental}`]: {
    backgroundColor: '#4CAF50'
  },

  [`& .${classes.networkManagement}`]: {
    backgroundColor: '#607D8B'
  },

  [`& .${classes.transportation}`]: {
    backgroundColor: '#795548'
  },

  [`& .${classes.water}`]: {
    backgroundColor: '#2196F3'
  },

  // Avatar colors for programs
  [`& .${classes.aviation}`]: {
    backgroundColor: '#795548'
  },

  [`& .${classes.conveyanceAndStorage}`]: {
    backgroundColor: '#2196F3'
  },

  [`& .${classes.drinkingWaterAndReuse}`]: {
    backgroundColor: '#03A9F4'
  },

  [`& .${classes.highwaysAndBridges}`]: {
    backgroundColor: '#795548'
  },

  [`& .${classes.portsAndMaritime}`]: {
    backgroundColor: '#795548'
  },

  [`& .${classes.transitAndRail}`]: {
    backgroundColor: '#795548'
  },

  [`& .${classes.urbanDevelopment}`]: {
    backgroundColor: '#607D8B'
  },

  [`& .${classes.wastewater}`]: {
    backgroundColor: '#03A9F4'
  },

  [`& .${classes.waterResourcesEcosystemMgmt}`]: {
    backgroundColor: '#CDDC39'
  },

  // Avatar colors for both
  [`& .${classes.noDataAvailable}`]: {
    backgroundColor: '#00338d'
  }
}));

export default function MagicMatrixCard({id, configuration, onLoading, onLoaded}) {


  const {t, i18n} = useTranslation();

  const persist = useSelector(state => state.persist);

  // Position: https://jacobsengineering.sharepoint.com/sites/CPOV012053/Lists/MagicMatrixPositions/MagicMatrixPositions.aspx
  // Project: https://jacobsengineering.sharepoint.com/sites/CPOV012053/Lists/ProjectMagicMatrix/ProjectMagicMatrix.aspx
  // Question: https://jacobsengineering.sharepoint.com/sites/CPOV012053/Lists/MagicMatrixQuestions/ProjectMatrixQuestions.aspx

  //     configuration: {
  //          dataSource: {
  //              positionName: <position datasource>
  //              projectName: <project datasource>
  //              questionName: <question datasource>
  //              fields: {
  //                  position: <position field name from position datasource>
  //              }
  //          }
  //          position: {
  //              value: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
  //              route:
  //              styleHeadingContainer: true | false
  //              styleContentContainer: true | false
  //          }
  //          project: {
  //              displayAvatar: true | false
  //              styleAvatar: true | false
  //          }
  //      }

  // DATASOURCE
  // -- DATASOURCE: Position
  const positionDataSourceName = configuration?.dataSource?.positionName ? configuration?.dataSource?.positionName : '';
  // -- DATASOURCE: Project
  const projectDataSourceName = configuration?.dataSource?.projectName ? configuration?.dataSource?.projectName : '';
  // -- DATASOURCE: Question
  const questionDataSourceName = configuration?.dataSource?.questionName ? configuration?.dataSource?.questionName : '';
  // -- DATASOURCE: Fields
  const positionField = configuration?.dataSource?.fields?.position ? configuration?.dataSource?.fields?.position : '';
  // ELEMENT
  // -- ELEMENT: Position
  const positionValue = configuration?.element?.position?.value ? configuration?.element?.position?.value : '';
  const positionRoute = configuration?.element?.position?.route ? configuration?.element?.position?.route : '';
  const positionStyleHeadingContainer = configuration?.element?.position?.styleHeadingContainer !== undefined ? configuration?.element?.position?.styleHeadingContainer : false;
  const positionStyleContentContainer = configuration?.element?.position?.styleContentContainer !== undefined ? configuration?.element?.position?.styleContentContainer : false;
  // -- ELEMENT: Project
  const projectDisplayAvatar = configuration?.element?.project?.displayAvatar !== undefined ? configuration?.element?.project?.displayAvatar : false;
  const projectStyleAvatar = configuration?.element?.project?.styleAvatar !== undefined ? configuration?.element?.project?.styleAvatar : false;

  const errorConfiguration = {
    type: 'nodata',
    display: 'textonly'
  };

  // App State
  const positionDataSource = useDataSource(positionDataSourceName);
  const projectDataSource = useDataSource(projectDataSourceName);
  const questionDataSource = useDataSource(questionDataSourceName);

  // Component State
  const [cardData, setCardData] = useState(null);
  const [projectCardFlipped, setCardFlip] = useState(false);
  const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);

  // Loading
  useEffect(() => {
    if (positionDataSource.fetching === true || projectDataSource.fetching === true || questionDataSource.fetching === true) {
      if (isFunction(onLoading)) {
        onLoading();
      }
    }
  }, [positionDataSource.fetching, projectDataSource.fetching, questionDataSource.fetching]);

  // Loaded
  useEffect(() => {
    if (positionDataSource.fetching === false && positionDataSource.fetched === true && projectDataSource.fetching === false && projectDataSource.fetched === true && questionDataSource.fetching === false && questionDataSource.fetched === true) {
      if (isFunction(onLoaded)) {
        onLoaded();
      }
    }
  }, [positionDataSource.fetched, projectDataSource.fetched, questionDataSource.fetched]);

  // Data
  useEffect(() => {

    if (positionDataSource.error || projectDataSource.error || questionDataSource.error) {

      // console.log('-----> MagicMatrixCard:useEffect(): dataSource -- ERROR');
      // TODO: Error component

    } else if (positionDataSource.data && positionDataSource.fetched &&
        projectDataSource.data && projectDataSource.fetched &&
        questionDataSource.data && questionDataSource.fetched) {

      // Projects
      const cardProjects = projectDataSource.data.filter(p => p.position === positionValue);

      // Add avatars to projects
      for (const p of cardProjects) {
        p.locationAvatar = getLocationAvatar(p.location);
        p.practiceAvatar = getPracticeAvatar(p.practice, projectStyleAvatar);
      }

      // Questions
      let questions = [];
      if (questionDataSource.data) {
        questions = questionDataSource.data.filter(f => f[positionField] === positionValue);
      }

      // Position Info
      let positionInfos = [];
      if (positionDataSource.data) {
        positionInfos = positionDataSource.data.filter(f => f[positionField] === positionValue);
      }
      const positionInfo = positionInfos.length > 0 ? positionInfos[0] : {
        position: 0,
        description: '-',
        complexity: '-',
        decisionmaking: '-',
        managementtype: '-'
      };

      // Setup card data
      const cardInfo = {
        position: positionInfo.position,
        description: positionInfo.description,
        complexity: positionInfo.complexity,
        decisionmaking: positionInfo.decisionmaking,
        managementtype: positionInfo.managementtype,
        projects: cardProjects,
        questions: questions
      }

      // Set component state
      setCardData(cardInfo);
    } else {

      // console.log('-----> MagicMatrixCard:useEffect(): dataSource -- OTHER');
      // TODO: Error component
    }

  }, [projectDataSource.data]);

    const getLocationAvatar = (value) => {

    // console.log('> MagicMatrixCard: getLocationAvatar()');
    // console.log('  Location: {0}'.format(value));

    switch(value.toLowerCase()) {
      case 'north':
        return 'N';
      case 'south':
        return 'S';
      case 'central':
        return 'C';
      default:
        return '?';
    }
  };

  const getLocationAvatar_Programs =  (value, styleAvatar) => {

    // https://www.countryflags.com/en/

    const extension = '.png';
    const path = './images/Avatars/';

    switch(value.toLowerCase()) {
      case 'brazil':
        return path + 'brazil-flag-round-icon-64' + extension;
      case 'canada':
        return path + 'canada-flag-round-icon-64' + extension;
      case 'germany':
        return path + 'germany-flag-round-icon-64' + extension;
      case 'india':
        return path + 'india-flag-round-icon-64' + extension;
      case 'ireland':
        return path + 'ireland-flag-round-icon-64' + extension;
      case 'philippines':
        return path + 'philippines-flag-round-icon-64' + extension;
      case 'puerto rico':
        // return path + 'XXXXX-flag-round-icon-64' + extension;
      case 'qatar':
        return path + 'qatar-flag-round-icon-64' + extension;
      case 'singapore':
        return path + 'singapore-flag-round-icon-64' + extension;
      case 'south korea':
        return path + 'south-korea-flag-round-icon-64' + extension;
      case 'united arab emirates':
        return path + 'united-arab-emirates-flag-round-icon-64' + extension;
      case 'united kingdom':
        return path + 'united-kingdom-flag-round-icon-64' + extension;
      case 'united states':
        return path + 'united-states-flag-round-icon-64' + extension;
      default:
        return '?';
    }
  };

  const getPracticeAvatar = (value, styleAvatar) => {

    // console.log('> MagicMatrixCard: getPracticeAvatar()');
    // console.log('  Department: {0}'.format(value));
    // console.log('  Style Avatar: {0}'.format(styleAvatar));

    const avatar = [];
    switch(value.toLowerCase()) {
      case 'education':
        avatar.push(<School />, styleAvatar ? classes.education : classes.gray);
        break;
      case 'environmental':
        avatar.push(<LocalFlorist />, styleAvatar ? classes.environmental : classes.gray);
        break;
      case 'network':
        avatar.push(<GridOn />, styleAvatar ? classes.networkManagement : classes.gray);
        break;
      case 'transportation':
        avatar.push(<LocalShipping />, styleAvatar ? classes.transportation : classes.gray);
        break;
      case 'water':
        avatar.push(<Waves />, styleAvatar ? classes.water : classes.gray);
        break;
      default:
        avatar.push(<HelpOutline />, classes.gray);
    }

    // console.log('  Avatar: ', avatar);
    return avatar;
  };

  const getPracticeAvatar_Programs = (value, styleAvatar) => {

    // console.log('> MagicMatrixCard: getPracticeAvatar()');
    // console.log('  Practice: {0}'.format(value));
    // console.log('  Style Avatar: {0}'.format(styleAvatar));

    const avatar = [];
    switch(value.toLowerCase()) {
      case 'aviation (tr)':
        avatar.push(<Flight />, styleAvatar ? classes.aviation : classes.gray);
        break;
      case 'conveyance and storage (wt)':
        avatar.push(<Waves />, styleAvatar ? classes.conveyanceAndStorage : classes.gray);
        break;
      case 'drinking water and reuse (wt)':
        avatar.push(<LocalDrink />, styleAvatar ? classes.drinkingWaterAndReuse : classes.gray);
        break;
      case 'highways and bridges (tr)':
        avatar.push(<LocalShipping />, styleAvatar ? classes.highwaysAndBridges : classes.gray);
        break;
      case 'ports and maritime (tr)':
        avatar.push(<DirectionsBoat />, styleAvatar ? classes.portsAndMaritime : classes.gray);
        break;
      case 'transit and rail (tr)':
        avatar.push(<Train />, styleAvatar ? classes.transitAndRail : classes.gray);
        break;
      case 'urban development':
        avatar.push(<LocationCity />, styleAvatar ? classes.urbanDevelopment : classes.gray);
        break;
      case 'wastewater (wt)':
        avatar.push(<RestoreFromTrash />, styleAvatar ? classes.wastewater : classes.gray);
        break;
      case 'water resources/ecosystem mgmt (wt)':
        avatar.push(<LocalFlorist />, styleAvatar ? classes.waterResourcesEcosystemMgmt : classes.gray);
        break;
      default:
        avatar.push(<HelpOutline />, classes.gray);
    }

    // console.log('  Avatar: ', avatar);
    return avatar;
  };

  const getHeaderContainerStyles = (value, styleHeadingContainer) => {

    // console.log('> MagicMatrixCard: getHeaderContainerStyles()');
    // console.log('  - Position: {0}'.format(value));
    // console.log('  - Style Heading: {0}.format(styleHeadingContainer));

    let styleClasses = [];
    if (styleHeadingContainer) {
      switch (value) {
        case 1:
          styleClasses.push(classes.headerContainerRed, classes.fontColorWhite);
          break;
        case 2:
          styleClasses.push(classes.headerContainerRed, classes.fontColorWhite);
          break;
        case 3:
          styleClasses.push(classes.headerContainerAmber, classes.fontColorWhite);
          break;
        case 4:
          styleClasses.push(classes.headerContainerRed, classes.fontColorWhite);
          break;
        case 5:
          styleClasses.push(classes.headerContainerAmber, classes.fontColorWhite);
          break;
        case 6:
          styleClasses.push(classes.headerContainerGreen, classes.fontColorWhite);
          break;
        case 7:
          styleClasses.push(classes.headerContainerAmber, classes.fontColorWhite);
          break;
        case 8:
          styleClasses.push(classes.headerContainerGreen, classes.fontColorWhite);
          break;
        case 9:
          styleClasses.push(classes.headerContainerGreen, classes.fontColorWhite);
          break;
        default:
          styleClasses.push(classes.headerContainerGrey, classes.fontColorBlack);
      }
    } else {
      styleClasses.push(classes.headerContainerWhite, classes.fontColorBlack);
      // styleClasses.push(classes.headerContainerGrey, classes.fontColorBlack);
    }

    // console.log('  Style classes: ', styleClasses);
    return styleClasses;
  }

  const getContentContainerStyles = (value, styleContentContainer) => {

    // console.log('> MagicMatrixCard: getContentContainerStyles()');
    // console.log('  - Position: {0}'.format(value));
    // console.log('  - Style Content: {0}'.format(styleContentContainer));

    let styleClasses = [];
    if (styleContentContainer) {
      switch (value) {
        case 1:
          styleClasses.push(classes.contentContainerRed);
          break;
        case 2:
          styleClasses.push(classes.contentContainerRed);
          break;
        case 3:
          styleClasses.push(classes.contentContainerAmber);
          break;
        case 4:
          styleClasses.push(classes.contentContainerRed);
          break;
        case 5:
          styleClasses.push(classes.contentContainerAmber);
          break;
        case 6:
          styleClasses.push(classes.contentContainerGreen);
          break;
        case 7:
          styleClasses.push(classes.contentContainerAmber);
          break;
        case 8:
          styleClasses.push(classes.contentContainerGreen);
          break;
        case 9:
          styleClasses.push(classes.contentContainerGreen);
          break;
        default:
          styleClasses.push(classes.contentContainerGrey);
      }
    } else {
      styleClasses.push(classes.contentContainerWhite);
      // styleClasses.push(classes.contentContainerGrey);
    }

    // console.log('  Style classes: ', styleClasses);
    return styleClasses;
  }

  let flipperClasses = 'flipper';
  if (projectCardFlipped) {
    flipperClasses = 'flipper flipped';
  }

  function ListItemLink(props) {
    return <ListItem button component='a' {...props} />;
  }

  function isFunction(functionToCheck) {
    return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
  }

  return (
    <React.Fragment>
    {/*<Box>*/}
      {cardData &&
      <Box className="magic-flip-container" style={{height: '100%'}}>

        {showProgress && (positionDataSource.fetching || projectDataSource.fetching || questionDataSource.fetching) &&
        <LinearProgress className={classes.linearProgress}/>
        }
        {showProgress && (!positionDataSource.fetching || !projectDataSource.fetching || !questionDataSource.fetching) &&
        <Box className={classes.linearProgress}></Box>
        }

        <Box className={flipperClasses} style={{height: '100%'}}>
          <Box className="front"
               style={{height: '100%', width: '100%', display: 'flex'}}>
            <Root className={[classes.root, getContentContainerStyles(cardData.position, positionStyleContentContainer)].join(' ')}>
              {/*Header container*/}
              <Box className={classes.headerContainer}>
                <Box className={getHeaderContainerStyles(cardData.position, positionStyleHeadingContainer)}
                     border={1}
                     borderRadius={1}
                     boxShadow={0}
                     display={'flex'}
                     justifyContent={'left'}
                     height={'100%'}>
                  {/*Position*/}
                  <Box className={classes.positionContainer}
                       boxShadow={0}
                       display={'flex'}
                       justifyContent={'center'}>
                    <Typography className={[classes.positionContent, getHeaderContainerStyles(cardData.position, positionStyleHeadingContainer)].join(' ')}
                                align={'center'}
                                component={'p'}
                                display={'initial'}
                                gutterBottom={false}
                                noWrap={false}
                                paragraph={false}
                                variant={'h3'}>
                      { cardData.position }
                    </Typography>
                  </Box>
                  {/*Complexity*/}
                  <Box className={classes.complexityContainer}>
                    <Typography className={getHeaderContainerStyles(cardData.position, positionStyleHeadingContainer)}
                                align={'center'}
                                component={'p'}
                                gutterBottom={false}
                                noWrap={false}
                                paragraph={false}
                                variant={'overline'}>
                      { 'Complexity' }
                    </Typography>
                    <Typography className={[classes.fontWeight500, getHeaderContainerStyles(cardData.position, positionStyleHeadingContainer)].join(' ')}
                                align={'center'}
                                component={'p'}
                                gutterBottom={false}
                                noWrap={false}
                                paragraph={false}
                                variant={'body1'}>
                      { cardData.complexity }
                    </Typography>
                  </Box>
                  {/*Decision Making*/}
                  <Box className={classes.decisionMakingContainer}>
                    <Typography className={getHeaderContainerStyles(cardData.position, positionStyleHeadingContainer)}
                                align={'center'}
                                component={'p'}
                                gutterBottom={false}
                                noWrap={false}
                                paragraph={false}
                                variant={'overline'}>
                      { 'Decision Making' }
                    </Typography>
                    <Typography className={[classes.fontWeight500, getHeaderContainerStyles(cardData.position, positionStyleHeadingContainer)].join(' ')}
                                align={'center'}
                                component={'p'}
                                gutterBottom={false}
                                noWrap={false}
                                paragraph={false}
                                variant={'body1'}>
                      { cardData.decisionmaking }
                    </Typography>
                  </Box>

                </Box>

              </Box>
              {/*Content container: Project list*/}
              <Box className={classes.contentContainer}>
                {(cardData.projects.length === 0) &&
                    <SmartError configuration={errorConfiguration} />
                }
                <List className={classes.list}>
                  {cardData.projects.map(row => (
                      <ListItemLink key={row.id}
                                    dense={true}
                                    divider={false}
                                    href={'/details/' + row.identifier}>
                        {projectDisplayAvatar &&
                        <ListItemAvatar><Avatar className={row.practiceAvatar[1]}>{row.practiceAvatar[0]}</Avatar></ListItemAvatar>
                        }
                        <ListItemText
                            primary={<Typography className={classes.listItemPrimary} color={'textPrimary'}>{ row.name }</Typography>}
                            secondary={<Typography className={classes.listItemSecondary} color={'textSecondary'}>{ row.projectmanager }</Typography>}>
                        </ListItemText>
                      </ListItemLink>
                  ))}
                </List>
              </Box>
              {/*Actions container*/}
              <Box className={classes.actionsContainer}>
                <Box style={{display: 'block'}}>
                  <IconButton aria-label={'flip'}
                              color={persist.themeLight ? 'primary' : 'secondary'} size={'small'}
                              onClick={() => setCardFlip(projectCardFlipped ? false : true)}>
                    <Flip />
                  </IconButton>
                </Box>
              </Box>
            </Root>
          </Box>
          <Box className="back"
               style={{height: '100%', width: '100%', display: 'flex'}}>
            <Root className={[classes.root, getContentContainerStyles(cardData.position, positionStyleContentContainer)].join(' ')}>
              {/*Header container*/}
              <Box className={classes.headerContainer}>
                <Box className={getHeaderContainerStyles(cardData.position, positionStyleHeadingContainer)}
                     border={1}
                     borderRadius={1}
                     boxShadow={0}
                     display={'flex'}
                     justifyContent={'left'}>
                  {/*Position*/}
                  <Box className={classes.positionContainer}
                       boxShadow={0}
                       display={'flex'}
                       justifyContent={'center'}>
                    <Typography className={[classes.positionContent, getHeaderContainerStyles(cardData.position, positionStyleHeadingContainer)].join(' ')}
                                align={'center'}
                                component={'p'}
                                display={'initial'}
                                gutterBottom={false}
                                noWrap={false}
                                paragraph={false}
                                variant={'h3'}>
                      { cardData.position }
                    </Typography>
                  </Box>
                  {/*Complexity*/}
                  <Box className={classes.complexityContainer}>
                    <Typography className={getHeaderContainerStyles(cardData.position, positionStyleHeadingContainer)}
                                align={'center'}
                                component={'p'}
                                gutterBottom={false}
                                noWrap={false}
                                paragraph={false}
                                variant={'overline'}>
                      { 'Complexity' }
                    </Typography>
                    <Typography className={[classes.fontWeight500, getHeaderContainerStyles(cardData.position, positionStyleHeadingContainer)].join(' ')}
                                align={'center'}
                                component={'p'}
                                gutterBottom={false}
                                noWrap={false}
                                paragraph={false}
                                variant={'body1'}>
                      { cardData.complexity }
                    </Typography>
                  </Box>
                  {/*Decision Making*/}
                  <Box className={classes.decisionMakingContainer}>
                    <Typography className={getHeaderContainerStyles(cardData.position, positionStyleHeadingContainer)}
                                align={'center'}
                                component={'p'}
                                gutterBottom={false}
                                noWrap={false}
                                paragraph={false}
                                variant={'overline'}>
                      { 'Decision Making' }
                    </Typography>
                    <Typography className={[classes.fontWeight500, getHeaderContainerStyles(cardData.position, positionStyleHeadingContainer)].join(' ')}
                                align={'center'}
                                component={'p'}
                                gutterBottom={false}
                                noWrap={false}
                                paragraph={false}
                                variant={'body1'}>
                      { cardData.decisionmaking }
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {/*Content container: Question list*/}
              <Box className={classes.contentContainer}>
                <List className={classes.list}>
                  {cardData.questions.map(row => (
                      <ListItemLink key={row.id}
                                    dense={true}
                                    divider={true}>
                        <ListItemText
                            primary={<Typography className={classes.listItemPrimary} color={'textPrimary'}>{ row.category }</Typography>}
                            secondary={<Typography className={classes.listItemSecondary} color={'textSecondary'}>{ row.question }</Typography>}>
                        </ListItemText>
                      </ListItemLink>
                  ))}
                </List>
              </Box>
              {/*Actions container*/}
              <Box className={classes.actionsContainer}>
                <Box style={{display: 'block'}}>
                  <IconButton aria-label={'flip'}
                              color={persist.themeLight ? 'primary' : 'secondary'} size={'small'}
                              onClick={() => setCardFlip(projectCardFlipped ? false : true)}>
                    <Flip />
                  </IconButton>
                </Box>
              </Box>
            </Root>
          </Box>
        </Box>

      </Box>
      }
    {/*</Box>*/}
    </React.Fragment>
  );
}
